import React, { useRef, useLayoutEffect } from "react";
import {
  MESSAGE_ANIMATION_TYPES,
  animationDurationSec,
  resolveMessageAnimation,
} from "../../utils/messageAnimation";

function animCssVar(design) {
  return { "--fsb-anim-duration": `${animationDurationSec(design)}s` };
}

/** Full pass: text enters at right edge, exits fully off the left. */
function measureTickerDistance(viewport) {
  const track = viewport?.querySelector(".fsb-widget-msg-ticker__track");
  const text = track?.querySelector(".fsb-widget-msg-ticker__text");
  if (!track || !text || !viewport) return;
  const distance = viewport.offsetWidth + text.offsetWidth;
  track.style.setProperty("--fsb-ticker-distance", `${distance}px`);
}

function TickerBlock({ message, style, design, wrapperClass }) {
  const viewportRef = useRef(null);
  const textStyle = { ...style, margin: 0, textAlign: "left", whiteSpace: "nowrap" };

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const measure = () => measureTickerDistance(viewport);
    measure();

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(viewport);
    window.addEventListener("resize", measure);

    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [message, style?.fontSize, style?.fontWeight]);

  return (
    <div
      ref={viewportRef}
      className={wrapperClass}
      style={{
        ...animCssVar(design),
        marginTop: style?.marginTop ?? 0,
        marginBottom: style?.marginBottom ?? 8,
      }}
    >
      <div className="fsb-widget-msg-ticker__track">
        <span className="fsb-widget-msg-ticker__text" style={textStyle}>
          {message}
        </span>
      </div>
    </div>
  );
}

function ShimmerBlock({ message, style, design, wrapperClass }) {
  return (
    <div
      className={wrapperClass}
      style={{
        ...animCssVar(design),
        marginTop: style?.marginTop ?? 0,
        marginBottom: style?.marginBottom ?? 8,
        textAlign: "center",
      }}
    >
      <p className="fsb-widget-msg-shimmer__text" style={{ ...style, margin: 0, position: "relative", zIndex: 1 }}>
        {message}
      </p>
      <span className="fsb-widget-msg-shimmer__beam" aria-hidden="true" />
    </div>
  );
}

function PulseBlock({ message, style, design, wrapperClass }) {
  return (
    <p className={wrapperClass} style={{ ...style, ...animCssVar(design) }}>
      {message}
    </p>
  );
}

function FloatBlock({ message, style, design, wrapperClass }) {
  return (
    <p className={wrapperClass} style={{ ...style, ...animCssVar(design) }}>
      {message}
    </p>
  );
}

/** Animated shipping bar message — ticker, shimmer, pulse, or float. */
export default function AnimatedMessage({ message, style, design, classPrefix = "fsb-widget-msg-anim" }) {
  if (!message) return null;

  const type = resolveMessageAnimation(design)?.type;

  switch (type) {
    case MESSAGE_ANIMATION_TYPES.TICKER_RTL:
      return (
        <TickerBlock
          message={message}
          style={style}
          design={design}
          wrapperClass={`${classPrefix} fsb-widget-msg-ticker`}
        />
      );
    case MESSAGE_ANIMATION_TYPES.SHIMMER_SWEEP:
      return (
        <ShimmerBlock
          message={message}
          style={style}
          design={design}
          wrapperClass={`${classPrefix} fsb-widget-msg-shimmer`}
        />
      );
    case MESSAGE_ANIMATION_TYPES.PULSE_SOFT:
      return (
        <PulseBlock
          message={message}
          style={style}
          design={design}
          wrapperClass={`${classPrefix} fsb-widget-msg-pulse`}
        />
      );
    case MESSAGE_ANIMATION_TYPES.FLOAT_BOUNCE:
      return (
        <FloatBlock
          message={message}
          style={style}
          design={design}
          wrapperClass={`${classPrefix} fsb-widget-msg-float`}
        />
      );
    default:
      return (
        <p className="fsb-widget-msg" style={style}>
          {message}
        </p>
      );
  }
}
